import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, type InboundCall, type InboundCallError, type InboundCallUser } from "@/api/client";
import { Icon } from "@/components/Icon";
import { Spinner } from "@/components/Spinner";
import { useOpenCandidate } from "@/pages/Candidates/candidateLink";
import { CallWaves, fmtDur } from "@/pages/Chats/CallModal";
import type { Softphone } from "@/pages/Chats/softphone";
import { useAudioDevices } from "@/pages/Chats/useAudioDevices";
import { MOBILE_BASE, isMobilePath } from "@/mobile/device";
import { isTrayPath, postToHost } from "@/tray/bridge";
import { CallerAvatar } from "./CallerAvatar";
import {
  callerNumber,
  callerTitle,
  droppedText,
  hangsUpLine,
  ivrLabel,
  listenErrorText,
  listenersLabel,
  missingNumberHint,
  modalPhase,
  takeOutcome,
  takeWithRetry,
  takenText,
  type SessionCall,
} from "./inboundCalls";
import "@/pages/Chats/call.css";
import { CALL_INTERRUPTED_TEXT, useCallInterrupted } from "@/pages/Chats/callMode";
import { HoldButton, HoldHint } from "@/pages/Chats/HoldControls";
import { inCallLabel } from "@/pages/Chats/callHold";

/** Звонок, открытый в этой вкладке. Живёт в InboundCallHost: прослушку
 * запускает клик по всплывашке, ещё до того, как окно смонтируется. */
export interface InboundSession extends SessionCall {
  call: InboundCall;
  // Разовый токен прослушки; null — POST /listen ещё не ответил.
  token: string | null;
  // POST /listen не удался (409 — уже взяли, 410 — звонок кончился, …).
  listenError: InboundCallError | null;
  // Устройства, с которыми поднята текущая линия (запомненные на момент клика):
  // список в окне появляется позже, и окно сверяет выбор в нём с этими.
  micId: string;
  speakerId: string;
  // Номер попытки прослушки в этом окне; «Слушать снова» — следующая.
  attempt: number;
}

const MIC_FAILED = "Этот микрофон не отдаёт звук — в линии остался прежний.";

type Tone = "wait" | "live" | "err" | "done";
// Подложки строки состояния — те же, что у исходящего (call.css); «итог» —
// свой нейтральный: звонок кончился, это не ошибка.
const STATUS_CLASS: Record<Tone, string> = {
  wait: "call-status--wait",
  live: "call-status--live",
  err: "call-status--err",
  done: "icall-status--done",
};

/** Перерисовка раз в секунду, пока идут часы. */
function useTicker(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}

// Окно входящего звонка: прослушка автоответчика → «Перехватить звонок» →
// разговор. Та же модалка, что у исходящего (call.css), но без выбора номера
// и «Теста», а выбор микрофона и выхода работает на живой линии. Сворачивается
// в плашку в углу — чтобы во время разговора смотреть карточку кандидата.
export function InboundCallModal({
  session,
  me,
  phone,
  minimized,
  busy,
  onMinimize,
  onRetry,
  onClose,
}: {
  session: InboundSession;
  me: { id: string; full_name: string } | null;
  phone: Softphone;
  minimized: boolean;
  // Во вкладке идёт другой звонок (исходящий из карточки или ленты): второй
  // SIP-сеанс 101 из той же вкладки поднимать нельзя.
  busy: boolean;
  onMinimize: (minimized: boolean) => void;
  // Поднять прослушку заново (звонящий ещё в меню, а наша линия упала).
  onRetry: () => void;
  // final — у звонка есть итог (кончился, взят): его всплывашка больше не нужна.
  // Иначе звонящий ещё в меню, и всплывашка вернётся.
  onClose: (final: boolean) => void;
}) {
  const { call, token, listenError } = session;
  const status = phone.status;
  const meId = me?.id ?? null;
  const devices = useAudioDevices();
  const interrupted = useCallInterrupted();
  const openCandidate = useOpenCandidate();
  // Мобильная версия: карточки кандидата там нет — вместо неё чат звонящего.
  const { pathname } = useLocation();
  const mobile = isMobilePath(pathname);
  // Окно программы для Windows: карточки здесь тоже нет — она открывается в
  // браузере, а окно звонка остаётся как есть, рядом.
  const tray = isTrayPath(pathname);
  const navigate = useNavigate();

  const [taking, setTaking] = useState(false);
  const [takeUnsure, setTakeUnsure] = useState(false);
  const [took, setTook] = useState(false);
  const [takenOther, setTakenOther] = useState<InboundCallUser | null>(null);
  const [gone, setGone] = useState(false); // take ответил 404/410
  const [listenLost, setListenLost] = useState(false); // take ответил 409 listen_lost
  const [takeError, setTakeError] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);

  const { phase, taker } = modalPhase({
    call,
    line: status.state,
    listenError,
    me: meId,
    takeUnsure,
    took,
    taking,
    takenOther,
    gone,
    listenLost,
  });
  const lost = phase === "ended" || phase === "taken_other";
  const terminal = lost || phase === "error" || phase === "dropped";
  const ticking = phase === "listening" || phase === "taking" || phase === "in_call";
  const now = useTicker(ticking);
  const lineUp = status.state === "listening" || status.state === "in_call";
  const canRetry = (phase === "error" || phase === "dropped") && call.state === "ringing" && call.intercept_ready;

  // Закрытие и софтфон — через ref: хост перерисовывается от каждого события
  // звонка, а таймеры ниже не должны из-за этого сбрасываться.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const phoneRef = useRef(phone);
  phoneRef.current = phone;

  // Звонок ушёл (взяли другие, звонящий положил трубку) — свою линию
  // прослушки кладём сами, не дожидаясь BYE от АТС. Из прослушки это вернёт
  // софтфон в idle — фаза при этом держится на состоянии звонка. Разговор
  // сюда не попадает никогда: пока он наш, фаза — «in_call» (modalPhase).
  useEffect(() => {
    if (!lost) return;
    if (hangsUpLine(phase, phoneRef.current.status.state)) void phoneRef.current.hangup();
  }, [lost]); // eslint-disable-line react-hooks/exhaustive-deps

  // Итог видно пару секунд, потом окно закрывается само (как у исходящего).
  useEffect(() => {
    if (!lost) return;
    const t = window.setTimeout(() => closeRef.current(true), 2500);
    return () => window.clearTimeout(t);
  }, [lost]);

  // ---- устройства ----
  // Микрофон, который сейчас в линии. Новая попытка прослушки поднимает линию
  // с запомненным — с него и начинаем сверку.
  const lineMicRef = useRef(session.micId);
  const micReqRef = useRef(0);
  useEffect(() => {
    lineMicRef.current = session.micId;
    // Новая попытка — с чистого листа: прежний перехват (в том числе
    // несостоявшийся) к новой линии отношения не имеет.
    setTaking(false);
    setTakeUnsure(false);
    setTook(false);
    setTakenOther(null);
    setGone(false);
    setListenLost(false);
    setTakeError(null);
    setMicError(null);
  }, [session.attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Выбор в списке и линия расходятся: рекрутёр выбрал другой микрофон, или
  // запомненный с тех пор отключили и список показал первый. Линию переводим
  // на показанный — но только когда она поднята (раньше подменять нечего;
  // выбор, сделанный пока подключались, применится здесь же, как только
  // линию подтвердят).
  useEffect(() => {
    const mics = devices.mics;
    const want = devices.micId;
    const have = lineMicRef.current;
    if (!lineUp || mics === null || !want || want === have) return;
    // Ничего не было запомнено, а в списке первым — системный «default»
    // Chrome: линия и так на нём, дёргать её незачем.
    if (!have && want === "default") {
      lineMicRef.current = want;
      return;
    }
    const req = ++micReqRef.current;
    void phoneRef.current.setMic(want).then((ok) => {
      if (req !== micReqRef.current) return; // уже выбрали следующий
      if (ok) {
        lineMicRef.current = want;
        setMicError(null);
        return;
      }
      setMicError(MIC_FAILED);
      // Список не должен врать: возвращаем в нём тот, что остался в линии.
      if (have && mics.some((d) => d.deviceId === have)) devices.setMicId(have);
    });
  }, [lineUp, devices.mics, devices.micId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Аудиовыход применяется сразу (setSinkId), но только к поднятой линии:
  // до неё софтфон всё равно возьмёт выход, с которым его запустили.
  useEffect(() => {
    if (!lineUp || devices.mics === null) return;
    phoneRef.current.setSpeaker(devices.speakerId);
  }, [lineUp, devices.mics, devices.speakerId]);

  const title = callerTitle(call);
  const number = callerNumber(call);
  const ivr = ivrLabel(call.ivr_step);
  const others = listenersLabel((call.listeners ?? []).filter((l) => l.user_id !== meId));
  const candidateId = call.person?.candidate_id ?? null;
  // Под заголовком: номер, если в заголовке имя; «нет в базе», если в
  // заголовке уже номер и человека мы не знаем; и что выбрано в меню.
  let numberPart: string | null = null;
  if (call.person?.name) numberPart = number;
  else if (!call.person && number) numberPart = "нет в базе";
  const subLine = [numberPart, ivr ? `выбрал: ${ivr}` : null].filter(Boolean).join(" · ");
  // Скрытый или нераспознанный номер: запись разговора ляжет в чат только по
  // номеру звонящего — предупреждаем заранее, до перехвата.
  const numberHint = missingNumberHint(call);

  // Успех — только 200. Без ответа — один повтор тем же токеном (бекенд
  // признаёт его своим и снова отвечает 200). 409 taken — даже с нашим именем
  // — это другая наша вкладка: здесь линия так и осталась прослушкой.
  const take = async () => {
    if (!token || taking || took || phase !== "listening") return;
    // Исход прошлой попытки так и не узнали — линию, возможно, уже перекинули
    // на звонящего.
    const wasUnsure = takeUnsure;
    setTaking(true);
    setTakeUnsure(true);
    setTakeError(null);
    const res = await takeWithRetry(() => api.inboundTake(call.id, token));
    if (res.ok) {
      setTook(true);
      setTakeUnsure(false);
      setTaking(false);
      phone.unmute();
      return;
    }
    const outcome = takeOutcome(res.err);
    switch (outcome.kind) {
      case "taken":
        setTakenOther(outcome.by);
        break;
      case "gone":
        setGone(true);
        break;
      case "lost":
        // Бекенд этой прослушки не знает — перехватить её нельзя уже никогда.
        // Кладём её сами; звонящий в меню, «Слушать снова» поднимет новую.
        // Кроме случая, когда прошлый перехват остался без ответа, а потом
        // chat-service перезапустился: линия, может быть, уже соединена со
        // звонящим, и наш BYE через Bridge(…, x) сбросил бы и его. Тогда
        // решает сама линия.
        setListenLost(true);
        if (!wasUnsure) void phone.hangup();
        break;
      case "error":
        // Остаёмся на прослушке: звонящий в меню, можно попробовать ещё.
        setTakeError(outcome.message);
        break;
    }
    setTakeUnsure(
      outcome.kind === "error" ? outcome.unsure : outcome.kind === "lost" && wasUnsure,
    );
    setTaking(false);
  };

  const openCard = () => {
    if (!candidateId) return;
    if (tray) {
      postToHost({ type: "open-url", url: `${window.location.origin}/candidates/${encodeURIComponent(candidateId)}` });
      return;
    }
    openCandidate(candidateId);
    // Карточка открывается под окном — сворачиваемся, чтобы её было видно.
    onMinimize(true);
  };
  const openChat = () => {
    const personId = call.person?.id;
    if (!personId) return;
    navigate(`${MOBILE_BASE}?chat=${encodeURIComponent(personId)}`);
    onMinimize(true);
  };

  // ---- строка состояния ----
  const since = status.state === "listening" || status.state === "in_call" ? status.since : now;
  const clock = fmtDur(Math.max(0, Math.floor((now - since) / 1000)));
  let statusText = "";
  let tone: Tone = "wait";
  switch (phase) {
    case "connecting":
      statusText = "Подключаемся…";
      break;
    case "listening":
      statusText = `Слушаете автоответчик · ${clock}`;
      break;
    case "taking":
      statusText = "Перехватываем…";
      break;
    case "in_call":
      statusText = inCallLabel(clock, { held: phone.held, muted: phone.micMuted });
      tone = phone.held ? "wait" : "live";
      break;
    case "ended": {
      const d = status.state === "ended" ? status.duration_s : 0;
      statusText = d ? `Звонок завершён · ${fmtDur(d)}` : "Звонок завершён";
      tone = "done";
      break;
    }
    case "dropped":
      statusText = droppedText({ took, listenLost });
      tone = "done";
      break;
    case "taken_other":
      statusText = takenText(taker ?? { user_id: "", name: "" }, meId);
      tone = "done";
      break;
    case "error":
      statusText = listenError
        ? listenErrorText(listenError)
        : status.state === "error"
          ? status.message
          : "Что-то пошло не так.";
      tone = "err";
      break;
  }

  const canTake = phase === "listening" && !!token;
  const takeBtn = (
    <button
      type="button"
      className="call-btn call-btn--call icall-take"
      onClick={take}
      disabled={!canTake}
      title={phase === "connecting" ? "Станет доступно, как только подключимся к звонку" : undefined}
    >
      {phase === "taking" ? <Spinner size={14} /> : <Icon name="phone" size={15} strokeWidth={1.8} />}
      {phase === "taking" ? "Перехватываем…" : "Перехватить звонок"}
    </button>
  );
  const hangBtn = (
    <button type="button" className="call-btn call-btn--hang" onClick={() => void phone.hangup()}>
      Завершить
    </button>
  );
  // «Без звука» — в разговоре (после перехвата); в прослушке микрофон и так
  // выключен, пока не перехватят. На удержании микрофон выключен самим
  // удержанием — кнопки нет.
  const muteBtn = phone.held ? null : (
    <button
      type="button"
      className={"call-btn call-btn--test call-mute" + (phone.micMuted ? " call-mute--on" : "")}
      onClick={() => phone.toggleMic()}
      aria-pressed={phone.micMuted}
      title={phone.micMuted ? "Звонящий вас не слышит" : "Выключить микрофон"}
    >
      {phone.micMuted ? "Включить микрофон" : "Без звука"}
    </button>
  );
  const close = () => onClose(lost);
  const retryBtn = (
    <button
      type="button"
      className="call-btn call-btn--call icall-take"
      onClick={onRetry}
      disabled={busy}
      title={busy ? "Вы уже на звонке — сначала завершите его." : undefined}
    >
      <Icon name="headphones" size={15} strokeWidth={1.8} />
      Слушать снова
    </button>
  );
  const closeBtn = (
    <button
      type="button"
      className={"call-btn call-btn--test icall-close-btn" + (terminal && !canRetry ? " icall-close-btn--wide" : "")}
      onClick={close}
      disabled={phase === "taking"}
      title={
        phase === "listening" || phase === "connecting" || phase === "dropped"
          ? "Звонящий останется в голосовом меню"
          : undefined
      }
    >
      Закрыть
    </button>
  );
  let actions: JSX.Element;
  // «Удержание» — после перехвата, как и «Без звука»; под ним собеседник
  // слышит музыку, а не тишину.
  const holdBtn = <HoldButton held={phone.held} onToggle={() => void phone.toggleHold()} />;
  if (phase === "in_call")
    actions = (
      <>
        {holdBtn}
        {muteBtn}
        {hangBtn}
      </>
    );
  else if (canRetry) {
    actions = (
      <>
        {closeBtn}
        {retryBtn}
      </>
    );
  } else if (terminal) actions = closeBtn;
  else {
    actions = (
      <>
        {closeBtn}
        {takeBtn}
      </>
    );
  }

  const ringing = phase === "listening" || phase === "connecting";

  if (minimized) {
    return (
      <section className="icall-mini" aria-label={`Звонок: ${title}`}>
        <div className="icall-mini-row">
          <CallerAvatar call={call} size={36} ringing={ringing} />
          <div className="icall-mini-text">
            <div className="icall-mini-title" title={title}>
              {title}
            </div>
            <div className={"icall-mini-status icall-mini-status--" + tone}>
              {(phase === "listening" || phase === "taking") && (
                <Icon name="headphones" size={12} strokeWidth={1.8} />
              )}
              <span>{statusText}</span>
            </div>
          </div>
          <button
            type="button"
            className="icall-icon-btn"
            onClick={() => onMinimize(false)}
            aria-label="Развернуть"
            title="Развернуть"
          >
            <Icon name="expand" size={15} />
          </button>
        </div>
        {phase === "in_call" && phone.held && <HoldHint since={phone.holdSince} />}
        {phase !== "ended" && phase !== "taken_other" && (
          <div className={"icall-mini-actions" + (phase === "in_call" ? " call-actions--stack" : "")}>{actions}</div>
        )}
      </section>
    );
  }

  return (
    <div className="modal-backdrop icall-backdrop" onClick={terminal ? close : undefined}>
      <div
        className="modal call-modal icall-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Входящий звонок: ${title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="icall-head">
          <CallerAvatar call={call} size={48} ringing={ringing} />
          <div className="icall-head-text">
            <div className="icall-eyebrow">Входящий звонок</div>
            <h2 className="call-title icall-head-title" title={title}>
              {title}
            </h2>
            {subLine && <div className="call-sub">{subLine}</div>}
          </div>
          <div className="icall-head-tools">
            <button
              type="button"
              className="icall-icon-btn"
              onClick={() => onMinimize(true)}
              aria-label="Свернуть"
              title="Свернуть"
            >
              <Icon name="minimize" size={16} />
            </button>
            {phase !== "in_call" && phase !== "taking" && (
              <button type="button" className="icall-icon-btn" onClick={close} aria-label="Закрыть" title="Закрыть">
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
        </header>

        <div className="call-body">
          {mobile
            ? call.person?.id && (
                <button type="button" className="icall-card-link" onClick={openChat}>
                  <Icon name="message" size={14} strokeWidth={1.8} />
                  Открыть чат
                  <Icon name="chevron-right" size={14} strokeWidth={1.8} />
                </button>
              )
            : candidateId && (
                <button type="button" className="icall-card-link" onClick={openCard}>
                  <Icon name="user" size={14} strokeWidth={1.8} />
                  Карточка кандидата
                  <Icon name="chevron-right" size={14} strokeWidth={1.8} />
                </button>
              )}

          {devices.mics === null && <Spinner center label="Устройства…" />}
          {devices.permError && <div className="call-warn">{devices.permError}</div>}
          {devices.mics !== null && !devices.permError && !devices.systemOnly && (
            <div className="icall-devices">
              {/* Выбор работает и на живой линии: микрофон подменяется в
                  отправителе (replaceTrack), выход — через setSinkId. */}
              <label className="call-field">
                <span className="call-label">Микрофон</span>
                <select
                  className="call-input"
                  value={devices.micId}
                  onChange={(e) => {
                    setMicError(null);
                    devices.setMicId(e.target.value);
                  }}
                >
                  {devices.mics.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="call-field">
                <span className="call-label">Аудиовыход</span>
                <select
                  className="call-input"
                  value={devices.speakerId}
                  onChange={(e) => devices.setSpeakerId(e.target.value)}
                  disabled={!devices.supportsSinkId}
                >
                  <option value="">По умолчанию</option>
                  {devices.speakers.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {!devices.supportsSinkId && (
                  <span className="call-hint">Браузер не даёт выбирать аудиовыход — системный по умолчанию.</span>
                )}
              </label>
            </div>
          )}
          {micError && <div className="call-warn">{micError}</div>}
          {interrupted && lineUp && <div className="call-warn">{CALL_INTERRUPTED_TEXT}</div>}
          {phone.audioBlocked && lineUp && (
            <button type="button" className="call-btn call-btn--call call-unmute" onClick={phone.resumeAudio}>
              Включить звук
            </button>
          )}

          <div className={"call-status icall-status " + STATUS_CLASS[tone]} role="status">
            {(phase === "listening" || phase === "taking") && <Icon name="headphones" size={15} strokeWidth={1.8} />}
            <span>{statusText}</span>
          </div>

          {(phase === "listening" || phase === "taking" || phase === "in_call") && (
            <div className="call-waves">
              <CallWaves local={phone.streams.local} remote={phone.streams.remote} />
            </div>
          )}
          {phase === "in_call" && phone.held && <HoldHint since={phone.holdSince} />}
          {phase === "in_call" && phone.micMuted && !phone.held && (
            <div className="icall-hint call-mute-hint" role="status">
              <Icon name="mic" size={13} strokeWidth={1.8} />
              Микрофон выключен: звонящий вас не слышит.
            </div>
          )}
          {(phase === "listening" || phase === "connecting" || phase === "taking") && (
            <div className="icall-hint">
              <Icon name="mic" size={13} strokeWidth={1.8} />
              Микрофон выключен: звонящий вас не слышит, пока вы не перехватите звонок.
            </div>
          )}
          {others && !terminal && (
            <div className="icall-hint">
              <Icon name="headphones" size={13} strokeWidth={1.8} />
              {others}
            </div>
          )}
          {numberHint && <div className="icall-hint">{numberHint}</div>}
          {canRetry && busy && (
            <div className="icall-hint">Вы уже на звонке — «Слушать снова» станет доступно после него.</div>
          )}
          {takeError && <div className="call-status call-status--err">{takeError}</div>}
        </div>

        <footer className={"call-actions" + (phase === "in_call" ? " call-actions--stack" : "")}>{actions}</footer>
      </div>
    </div>
  );
}
